'use client';

import { type ComponentProps, type ReactNode, useMemo, useState } from 'react';

import { cn } from '../../lib/utils';
import { Badge } from './badge';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  type TableCellProps,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAllHead,
  TableSelectCell,
  type TableSort,
} from './table';

export type DataTableColumn<Row> = {
  /** Stable id - also the sort key. */
  id: string;
  /** Header label. */
  header: ReactNode;
  /** Cell content for a row. */
  cell: (row: Row) => ReactNode;
  /** Figma cell type for the column's body cells (default `text`). */
  variant?: TableCellProps['variant'];
  /** Figma header "Align"; numeric columns default to right. */
  align?: 'left' | 'right';
  /** Width in px or a CSS length; leave one column without it to fill. */
  width?: number | string;
  /**
   * Value to sort by. Giving one makes the column sortable (click cycles
   * ascending → descending → unsorted).
   */
  sortValue?: (row: Row) => SortValue;
};

export type DataTableSortState = {
  column: string;
  direction: 'ascending' | 'descending';
};

export type DataTableProps<Row> = Omit<ComponentProps<'div'>, 'children'> & {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  /** Stable id per row (selection is kept by id). */
  getRowId: (row: Row) => string;
  /** Name of a row for its checkbox and actions ("Select Product teaser — v3"). */
  getRowLabel?: (row: Row) => string;
  /** Accessible name of the table (rendered as an `sr-only` caption). */
  label: string;
  /** Show the checkbox column. */
  selectable?: boolean;
  /** Selected row ids (controlled). */
  selected?: string[];
  /** Initial selection (uncontrolled). */
  defaultSelected?: string[];
  onSelectedChange?: (ids: string[]) => void;
  /** Sort (controlled); `null` is unsorted. */
  sort?: DataTableSortState | null;
  defaultSort?: DataTableSortState | null;
  onSortChange?: (sort: DataTableSortState | null) => void;
  /** Rows arrive sorted (server-side): don't sort them here. */
  manualSort?: boolean;
  /** Above the table - a `TableToolbar`. */
  toolbar?: ReactNode;
  /** Below the table - a `Pagination`. */
  footer?: ReactNode;
  /** Shown in place of the rows when there are none - an `EmptyState`. */
  empty?: ReactNode;
  /** Keep the header in view; give the scroller a height via `scrollClassName`. */
  stickyHeader?: boolean;
  scrollClassName?: string;
};

type SortValue = string | number | null | undefined;

/** Compare two sort values; blanks (null / undefined) go last in either direction. */
function compare(a: SortValue, b: SortValue, sign: 1 | -1) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return sign * (a - b);
  return sign * String(a).localeCompare(String(b), undefined, { numeric: true });
}

function useControllable<T>(value: T | undefined, initial: T, onChange?: (v: T) => void) {
  const [inner, setInner] = useState(initial);
  const current = value === undefined ? inner : value;
  const set = (next: T) => {
    if (value === undefined) setInner(next);
    onChange?.(next);
  };
  return [current, set] as const;
}

/**
 * The Figma "Example — Data Table" (59:115) as one component: a 12px-radius
 * bg/surface card with a 1px border/subtle stroke, holding a toolbar, the
 * header row, 56px rows and a pagination footer. Sorting and selection are
 * plain React state (controlled or not); pagination stays with the caller
 * (pass the page's rows and a `Pagination` footer). For anything this doesn't
 * cover, compose `Table` and its parts directly.
 */
export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  getRowLabel,
  label,
  selectable,
  selected: selectedProp,
  defaultSelected = [],
  onSelectedChange,
  sort: sortProp,
  defaultSort = null,
  onSortChange,
  manualSort,
  toolbar,
  footer,
  empty,
  stickyHeader,
  scrollClassName,
  className,
  ...props
}: DataTableProps<Row>) {
  const [selected, setSelected] = useControllable(
    selectedProp,
    defaultSelected,
    onSelectedChange,
  );
  const [sort, setSort] = useControllable(sortProp, defaultSort, onSortChange);

  const sortedRows = useMemo(() => {
    if (!sort || manualSort) return rows;
    const column = columns.find((c) => c.id === sort.column);
    if (!column?.sortValue) return rows;
    const value = column.sortValue;
    const sign = sort.direction === 'ascending' ? 1 : -1;
    return [...rows].sort((a, b) => compare(value(a), value(b), sign));
  }, [rows, columns, sort, manualSort]);

  const ids = sortedRows.map(getRowId);
  const selectedSet = new Set(selected);
  const selectedCount = ids.filter((id) => selectedSet.has(id)).length;
  const allState: boolean | 'indeterminate' =
    selectedCount === 0 ? false : selectedCount === ids.length ? true : 'indeterminate';

  const cycleSort = (column: string) => {
    if (sort?.column !== column) setSort({ column, direction: 'ascending' });
    else if (sort.direction === 'ascending') setSort({ column, direction: 'descending' });
    else setSort(null);
  };

  const toggleRow = (id: string, on: boolean) =>
    setSelected(
      on ? [...selected.filter((s) => s !== id), id] : selected.filter((s) => s !== id),
    );

  const toggleAll = (on: boolean) =>
    setSelected(
      on
        ? [...selected.filter((s) => !ids.includes(s)), ...ids]
        : selected.filter((s) => !ids.includes(s)),
    );

  const columnCount = columns.length + (selectable ? 1 : 0);

  return (
    <div
      data-slot="data-table"
      className={cn(
        'bg-bg-surface overflow-hidden rounded-lg border border-border-subtle',
        className,
      )}
      {...props}
    >
      {toolbar}
      <Table fixed stickyHeader={stickyHeader} containerClassName={scrollClassName}>
        <TableCaption className="sr-only">{label}</TableCaption>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableSelectAllHead
                checked={allState}
                onCheckedChange={toggleAll}
                disabled={ids.length === 0}
              />
            )}
            {columns.map((column) => {
              const align =
                column.align ?? (column.variant === 'numeric' ? 'right' : 'left');
              const direction: TableSort | undefined = column.sortValue
                ? sort?.column === column.id
                  ? sort.direction
                  : 'none'
                : undefined;
              return (
                <TableHead
                  key={column.id}
                  align={align}
                  width={
                    column.variant === 'actions' ? (column.width ?? 64) : column.width
                  }
                  sort={direction}
                  onSort={column.sortValue ? () => cycleSort(column.id) : undefined}
                >
                  {column.header}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length === 0 ? (
            <tr>
              <td colSpan={columnCount}>{empty}</td>
            </tr>
          ) : (
            sortedRows.map((row) => {
              const id = getRowId(row);
              const isSelected = selectedSet.has(id);
              const rowLabel = getRowLabel?.(row) ?? id;
              return (
                <TableRow key={id} selected={isSelected}>
                  {selectable && (
                    <TableSelectCell
                      label={`Select ${rowLabel}`}
                      checked={isSelected}
                      onCheckedChange={(on) => toggleRow(id, on)}
                    />
                  )}
                  {columns.map((column) => (
                    <TableCell key={column.id} variant={column.variant}>
                      {column.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {footer}
    </div>
  );
}

export type TableToolbarProps = Omit<ComponentProps<'div'>, 'title'> & {
  /** Heading/H4 title ("Reels"). */
  title: ReactNode;
  /** Neutral count Badge next to the title (total items). */
  count?: ReactNode;
  /** Heading element for the title. */
  titleAs?: 'h2' | 'h3' | 'h4';
  /** Right-aligned controls, 8px apart: a SearchField, Filter, the primary action. */
  children?: ReactNode;
};

/**
 * Figma Data Table "Toolbar": 64 tall, padding 0/16, 8px gap - the title with
 * its count Badge on the left, filters and the primary action on the right.
 */
export function TableToolbar({
  title,
  count,
  titleAs: Title = 'h2',
  className,
  children,
  ...props
}: TableToolbarProps) {
  return (
    <div
      data-slot="table-toolbar"
      className={cn('flex h-16 items-center gap-2 px-4', className)}
      {...props}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Title className="text-heading-h4 text-text-primary truncate">{title}</Title>
        {count !== undefined && <Badge>{count}</Badge>}
      </div>
      {children}
    </div>
  );
}
